// packages/web/src/lib/useKnotPushToTalk.ts — ported 2026-07-29 from the
// Rexx fork's 2026-07-21 build (Huan: "The Knot should know if it has been
// pushed and released or pushed and held. If it is held it should accept
// voice input and listen until the button is released. If the knot is
// quickly pushed and released it should popup the capture window").
//
// HOLD -> real mic capture (MediaRecorder), the Knot's own state machine
// drives visual feedback (listening -> understanding -> proposing ->
// confirmed). On release: upload + transcribe (/api/knot/transcribe —
// transcribeAudioBuffer, the same Whisper primitive Pythia's own voice-
// note ingestion already proved), then the transcript goes to
// /api/knot/intent — the CONTEXT-AWARE classify+resolve step: it knows
// which screen the Knot was held on and either creates directly
// (status:'ready' — nothing named, or the named thing IS the open record)
// or hands back to the caller for confirmation (status:'confirm' — "never
// assume": any named entity that differs from screen context, even an
// exact match, stops here) or 'unclear' (doesn't read as a create-command
// at all — falls back to the typed capture sheet with the transcript
// preserved verbatim, never dropped).
//
// TAP (released before the hold threshold) -> unchanged: opens the typed
// capture sheet.
//
// Generalized from the fork's version (Core hardening 2026-07-29, Item 6):
// the fork's `deriveItemName()` hardcoded a per-target-type field-name
// switch (client->name, site->site_name, work_order->title, ...) — that's
// vertical knowledge that doesn't belong in a supposedly-generic hook.
// Item 7's server route now computes `itemName` itself (via the target's
// declared `nameField`) and includes it directly in the response; this
// hook just reads `intent.itemName` — zero per-target knowledge left here.
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import {
  transcribeVoiceNote,
  knotIntent,
  knotIntentCreate,
  ApiError,
  type CaptureContext,
  type KnotCandidate,
  type KnotContextType,
  type KnotIntentResult,
  type KnotTargetType,
  type KnotVoiceRef,
} from './api';
import { enqueueCapture, listOutbox, type OutboxEntry } from './outbox';
import type { KnotState } from '../components/Knot';

const HOLD_THRESHOLD_MS = 280;
// Below this, a hold-then-immediate-release is almost certainly an
// accidental brush of the button, not a real utterance — don't submit
// silence/noise as a capture.
const MIN_RECORDING_BYTES = 400;
// S5d §2 — the toast learns to die. An error here is a transient
// (role="status"), not a modal: it leaves by itself after this long, on the
// next successful action of any kind, and on tap. Before this the string was
// hook state on a component that never unmounts, so it outlived reconnects
// and successful sends by minutes.
const ERROR_TOAST_MS = 6000;

export interface KnotContext {
  type: KnotContextType;
  id: string;
}

/* ------------------------------------------------------------------ *
 * CV4 §2 — WHEN THE TECH IS NOT RECORDING, THE OS MUST NOT SAY THEY ARE.
 *
 * This deliberately REVERTS CV2 §5b, which held the granted MediaStream at
 * module level between holds so iOS Safari in a browser tab would not
 * re-prompt for the microphone on every hold. The trade does not survive
 * contact with the phone: **iOS lights the recording indicator for as long
 * as a TRACK is live, not while a MediaRecorder runs.** So releasing the
 * Knot genuinely stopped the recording while the Dynamic Island went on
 * telling the tech he was still being listened to — and the only way it
 * ever went out was the tech tapping the island, which backgrounds the PWA,
 * which hit our own visibilitychange release. We built the lie and then
 * relied on the user's alarm at it to clean up after us.
 *
 * What §5b bought is worth close to nothing where this app actually lives:
 * on an INSTALLED PWA the mic grant is persistent, so there is no second
 * prompt to avoid — §5b's own header conceded that. What it cost is the
 * platform's clearest trust signal, on the one surface where a tech is
 * holding an open microphone inside somebody else's house.
 *
 * The revert is UNCONDITIONAL, not "browser-tab-only". A conditional would
 * keep the same lie alive on one surface, and the rule has no surface
 * clause. One code path: acquire per hold, stop the tracks on every exit
 * from a hold (cleanupAudio). The indicator is lit exactly while the
 * recording is running, which is the only state a phone should ever report.
 * ------------------------------------------------------------------ */

/** Releases the browser's pointer capture for a finished gesture. Throws
 * NotFoundError when this pointer was never captured (a synthetic event, a
 * cancel that arrived after the capture was already lost) — which is not a
 * failure of anything, so it is swallowed. */
function releaseCapture(e: PointerEvent): void {
  const target = e.currentTarget as (Element & { releasePointerCapture?: (id: number) => void }) | null;
  try {
    target?.releasePointerCapture?.(e.pointerId);
  } catch {
    /* was never captured — nothing to release */
  }
}

/**
 * S5d §1 — VOICE GOES THROUGH THE OUTBOX, where a caller gives it one.
 *
 * The walk's most important finding (Huan, 2026-08-14): the hold made three
 * unguarded network calls and treated every failure as terminal, so truly
 * offline the tech got "Could not send that — try again" at a dead radio —
 * while the TYPED path both pre-checked `navigator.onLine` and queued on a
 * non-ApiError throw. Voice is the tech's primary input; it was the one path
 * that lost words.
 *
 * A caller that passes this gives the hold the same two-branch discipline the
 * sheet has: offline pre-check before the transcribe call, and the post-hoc
 * rule (a non-ApiError failure ⇒ the request never landed ⇒ queue, not
 * shout). The AUDIO exists at failure time; the outbox stores File payloads
 * natively; the drain's endpoint transcribes audio on arrival. Nothing new is
 * built — this wires what existed.
 *
 * Callers that DON'T pass it (FirstRun, WorkOrderDrawer — both drive the
 * transcript short-circuit) keep today's behaviour on purpose: their holds
 * are not generic captures (a pack-scripted first-run target, a form-assist),
 * so queueing one into the generic capture outbox would file it somewhere the
 * screen never promised.
 */
export interface KnotOfflineCapture {
  /** The same context envelope the typed sheet would send right now. */
  getContext: () => CaptureContext;
  /** The queue took it. NOT an error: render the same receipt surface a
   *  queued typed capture shows. */
  onQueued: (entry: OutboxEntry, ahead: number) => void;
}

export interface KnotPendingConfirm {
  targetType: KnotTargetType;
  targetLabel: string;
  itemName: string;
  fields: Record<string, unknown>;
  parentType: KnotContextType;
  parentLabel: string;
  candidates: KnotCandidate[];
  suggestion: KnotCandidate | null;
}

/** CV5 §4 — the message a create shows when the words landed but the
 * RECORDING did not. Never silent: the recording was the point. */
const RECORDING_LOST = 'Saved what you said — but the recording couldn’t be attached to it.';

export interface UseKnotPushToTalkResult {
  knotState: KnotState;
  amp: number | undefined;
  error: string | null;
  /** C7: the mic is not available on this device/permission state —
   * getUserMedia refused, or MediaRecorder does not exist. A screen whose
   * COPY promises a hold reads this and says something else instead: an
   * instruction that cannot be followed is worse than no instruction. */
  micDenied: boolean;
  dismissError: () => void;
  pendingConfirm: KnotPendingConfirm | null;
  resolveConfirm: (parent: KnotCandidate) => void;
  cancelConfirm: () => void;
  onPointerDown: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
  /** CV4 §2 — the backstop every hold-bearing element must also wire: a
   * pointer can disappear without firing `up` OR `cancel`. */
  onLostPointerCapture: (e: PointerEvent) => void;
  onClick: () => void;
}

/**
 * C7 — `onTranscript` (optional) SHORT-CIRCUITS the intent step: the hold
 * ends at the transcript and hands it straight to the caller, which then
 * files it through whatever door it already knows about.
 *
 * That is the first-run capture's shape exactly, and the reason is C6b's
 * own (screens/FirstRun.tsx, and the FirstRunCaptureConfig header): the
 * step's target is PACK-DECLARED — "tell me what you did today" IS the
 * declared target's shape — so there is nothing to classify, and a first
 * run must never dead-end on a model's mood. Without this the same hold
 * would route through /api/knot/intent and could file at a DIFFERENT
 * target than the one the pack scripted, or stop on a confirm card the
 * first-run screen has no room for.
 *
 * Absent (every existing call site): unchanged — transcribe, classify,
 * create/confirm/unclear exactly as before.
 *
 * B16 §6 — `knotIntentEnabled` (default true, so every existing call site is
 * unchanged) is the deployment fact `getKnotIntentEnabled()` reads off
 * `GET /auth/entry`: whether the active pack declared a `knotIntent` config
 * at all. actor_assist declares none, so `POST /api/knot/intent` 404s on
 * every CallToWrap build — the catch below already treats that 404 exactly
 * like 'unclear' (fall back to the transcript-in-sheet path), so passing
 * `false` here just skips straight to the SAME fallback without spending the
 * round trip first.
 */
export function useKnotPushToTalk(
  tenantId: string,
  context: KnotContext | null,
  onQuickTap: () => void,
  onUnclear: (transcript: string) => void,
  onVoiceCaptured?: () => void,
  onTranscript?: (transcript: string) => void,
  offlineCapture?: KnotOfflineCapture,
  /** v1.0.3 (Huan's ruling 2026-08-30, second half): a 'ready' intent is
   * HANDED OVER as a proposal instead of created on the spot. Release is no
   * longer the approval gesture — the person sees what is about to be made
   * (the extracted fields, the parent) and says yes on a card, same as the
   * typed door. When absent, the old create-immediately behaviour stands so
   * existing callers are byte-identical. */
  onReadyIntent?: (
    intent: Extract<KnotIntentResult, { status: 'ready' }>,
    voice?: KnotVoiceRef
  ) => void,
  /** R-10 — the update lane's handoff: a spoken update (diff card or
   * record-pick) opens the sheet, exactly as onReadyIntent does for
   * creates. Absent = the transcript falls to the typed sheet as unclear. */
  onUpdateIntent?: (
    intent: Extract<KnotIntentResult, { status: 'update-ready' | 'update-confirm' }>,
    voice?: KnotVoiceRef
  ) => void,
  knotIntentEnabled: boolean = true
): UseKnotPushToTalkResult {
  const [knotState, setKnotState] = useState<KnotState>('idle');
  const [amp, setAmp] = useState<number>();
  const [error, setError] = useState<string | null>(null);
  const [micDenied, setMicDenied] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<
    (KnotPendingConfirm & { rawFields: Record<string, unknown>; voice?: KnotVoiceRef }) | null
  >(null);

  const holdTimer = useRef<number | null>(null);
  const isHolding = useRef(false);
  // Pointer down/up already decided the outcome (tap vs hold) — the click
  // event a real pointer sequence also fires afterward must be a no-op, or
  // a completed hold would ALSO reopen the capture sheet as a "tap".
  const pointerHandled = useRef(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const contextRef = useRef(context);
  contextRef.current = context;
  // Recreated by the caller every render; the ref keeps the hold's async
  // release reading the CURRENT one (same discipline as contextRef).
  const offlineCaptureRef = useRef(offlineCapture);
  offlineCaptureRef.current = offlineCapture;

  // §2 — every error in this hook gets the same three deaths: by itself
  // (ERROR_TOAST_MS), on the next success (callers of clearError), on tap
  // (dismissError). In the hook, not per-surface — all three consumers of the
  // error slot (App, FirstRun, WorkOrderDrawer) inherit it at once.
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearError = useCallback(() => {
    if (errorTimer.current !== null) {
      clearTimeout(errorTimer.current);
      errorTimer.current = null;
    }
    setError(null);
  }, []);
  const showError = useCallback((message: string) => {
    if (errorTimer.current !== null) clearTimeout(errorTimer.current);
    setError(message);
    errorTimer.current = setTimeout(() => {
      errorTimer.current = null;
      setError(null);
    }, ERROR_TOAST_MS);
  }, []);
  useEffect(
    () => () => {
      if (errorTimer.current !== null) clearTimeout(errorTimer.current);
    },
    []
  );

  const cleanupAudio = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    // CV4 §2: THE TRACKS STOP HERE, on every path out of a hold — this call
    // is what turns the OS recording indicator off. See the module header
    // for why the session-held stream is gone.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  const pollAmplitude = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(data);
    let sumSquares = 0;
    for (const v of data) {
      const d = (v - 128) / 128;
      sumSquares += d * d;
    }
    const rms = Math.sqrt(sumSquares / data.length);
    setAmp(Math.min(1, rms * 4));
    rafRef.current = requestAnimationFrame(pollAmplitude);
  }, []);

  const startRecording = useCallback(async () => {
    isHolding.current = true;
    try {
      // CV4 §2: acquired per hold, stopped on release — see module header.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isHolding.current) {
        // Released (or cancelled) before the permission prompt resolved.
        // Stop what we were just handed: a hold nobody is in must not leave
        // a live track — and a lit indicator — behind it.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;

      const AudioContextCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AudioContextCtor) {
        const ctx = new AudioContextCtor();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;
        pollAmplitude();
      }

      chunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      clearError();
      setMicDenied(false);
      setKnotState('listening');
    } catch {
      isHolding.current = false;
      cleanupAudio();
      setKnotState('idle');
      // Both halves of the same fact: the string SAYS it, and the flag
      // lets a screen stop promising a gesture that cannot work.
      setMicDenied(true);
      showError('Microphone unavailable — typing works too.');
    }
  }, [cleanupAudio, pollAmplitude, clearError, showError]);

  const stopRecordingAndSubmit = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    setKnotState('understanding');
    recorder.onstop = () => {
      cleanupAudio();
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
      mediaRecorderRef.current = null;

      if (blob.size < MIN_RECORDING_BYTES) {
        setKnotState('idle');
        return;
      }

      // S5d §1 — put what exists on the device, where a caller wired the
      // queue. Returns false when this hold has no queue (FirstRun /
      // WorkOrderDrawer — see KnotOfflineCapture's header) or the queue
      // itself failed, so the caller of THIS helper falls through to the
      // loud path instead of losing words silently.
      const keepOnDevice = async (text: string, files: File[]): Promise<boolean> => {
        const offline = offlineCaptureRef.current;
        if (!offline) return false;
        try {
          const entry = await enqueueCapture({
            tenantId,
            text,
            files,
            context: offline.getContext(),
          });
          const all = await listOutbox();
          const ahead = Math.max(0, all.findIndex((e) => e.id === entry.id));
          // A successful queue-on-device is a SUCCESS (§2): it clears the
          // error slot and lands on the same confirmed beat a sent capture
          // gets — the receipt beside it says which half has not happened.
          clearError();
          setKnotState('confirmed');
          setTimeout(() => setKnotState('idle'), 900);
          offline.onQueued(entry, ahead);
          return true;
        } catch {
          return false;
        }
      };
      // The send path dereferences `.name` (api.ts's captureKnot), so the
      // blob is wrapped before it is queued.
      const audioFile = () =>
        new File([blob], 'voice-note.webm', { type: blob.type || 'audio/webm' });

      void (async () => {
        // OFFLINE PRE-CHECK — the same branch the typed sheet has always
        // had. Transcription is server-side, so offline there is no
        // transcript; but the AUDIO exists, the outbox stores File payloads
        // natively, and the drain's endpoint transcribes on arrival. wf03
        // frame 3 is this case drawn: audio stored solid, the rest pending.
        if (
          typeof navigator !== 'undefined' &&
          navigator.onLine === false &&
          (await keepOnDevice('', [audioFile()]))
        ) {
          return;
        }

        // CV5 §4 — THE REF IS CAUGHT NOW.
        //
        // This route has ALWAYS stored the audio before transcribing and
        // handed back `{text, ref, mimeType}` — its own doc says the ref
        // "rides through capture exactly like a photo attachment". This line
        // destructured `text` and dropped the other two on the floor, so
        // every recording a tech ever made through the hold was stored,
        // referenced by nothing, and unreachable from every surface. Nothing
        // new is built here either: the ref is picked up and carried.
        let text: string;
        let voice: KnotVoiceRef | undefined;
        try {
          const heard = await transcribeVoiceNote(tenantId, blob, 'voice-note.webm');
          text = heard.text;
          voice = heard.ref
            ? { ref: heard.ref, mimeType: heard.mimeType, filename: 'voice-note.webm' }
            : undefined;
        } catch (err) {
          // The post-hoc half of the sheet's discipline: a non-ApiError
          // rejection means the request NEVER LANDED (offline, DNS, reset).
          // That is the normal case in a walk-in cooler — queue, don't shout.
          if (!(err instanceof ApiError) && (await keepOnDevice('', [audioFile()]))) return;
          setKnotState('idle');
          showError(err instanceof ApiError ? err.message : 'Could not send that — try again.');
          return;
        }

        if (text.trim() === '') {
          setKnotState('idle');
          showError('We couldn’t make that out — typing works too.');
          return;
        }

        // C7 — the caller already knows what the utterance is for.
        // Hand it over verbatim and stop: no classify, no target this
        // hook chose, nothing invented on the way.
        if (onTranscript) {
          setKnotState('idle');
          onTranscript(text);
          return;
        }

        // B16 §6 — don't ask a door that isn't there. Same fallback the
        // 404 catch below already lands on, taken straight rather than after
        // a round trip that was never going to answer anything else.
        if (!knotIntentEnabled) {
          setKnotState('idle');
          onUnclear(text);
          return;
        }

        try {
          // A pack that declares no knotIntent config leaves this route
          // unregistered, so the call 404s. That is a legitimate deployment —
          // the Personal pack has no vertical targets to resolve an utterance
          // against — and the locked Knot spec's own answer for a surface with
          // no primary object is to fall back to global capture rather than
          // guess. So treat "no intent engine here" exactly like 'unclear':
          // hand the transcript to the typed sheet, verbatim, never dropped.
          // Before this, the 404 fell to the catch below, which set an error
          // string NOTHING rendered — hold, speak, release, silence.
          let intent: Awaited<ReturnType<typeof knotIntent>>;
          try {
            intent = await knotIntent(tenantId, text, contextRef.current ?? undefined);
          } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
              setKnotState('idle');
              onUnclear(text);
              return;
            }
            throw err;
          }

          if (intent.status === 'unclear') {
            setKnotState('idle');
            onUnclear(text);
            return;
          }

          if (intent.status === 'ready') {
            // v1.0.3 — propose-guards-interpretation, restored to the voice
            // lane: when the caller can show a proposal card, the create
            // WAITS for a yes there. The transcript still rides along on the
            // voice ref (CV5 §4) so the retained recording carries our
            // reading of it.
            if (onReadyIntent) {
              // Idle, not 'proposing': the sheet drives the Knot's states
              // from here (its open effect says 'proposing'), and this
              // hook's non-idle state would override it for good.
              setKnotState('idle');
              onReadyIntent(intent, voice ? { ...voice, transcript: text } : undefined);
              return;
            }
            setKnotState('proposing');
            // CV5 §4: the transcript rides along so the retained recording
            // carries our reading of it — never a second transcription.
            const created = await knotIntentCreate(
              tenantId,
              intent.targetType,
              intent.fields,
              intent.parent ?? undefined,
              voice ? { ...voice, transcript: text } : undefined
            );
            setKnotState('confirmed');
            onVoiceCaptured?.();
            setTimeout(() => setKnotState('idle'), 900);
            // The words are safe either way; if the audio is not, SAY it.
            if (voice && created.recording == null) showError(RECORDING_LOST);
            return;
          }

          // R-10 — an update: hand the whole thing to the sheet (diff card
          // or record-pick), recording ref carried. With no consumer wired,
          // honest fallback: the words go to the typed sheet, never dropped.
          if (intent.status === 'update-ready' || intent.status === 'update-confirm') {
            setKnotState('idle');
            if (onUpdateIntent) {
              onUpdateIntent(intent, voice ? { ...voice, transcript: text } : undefined);
            } else {
              onUnclear(text);
            }
            return;
          }

          // status === 'confirm' — "never assume": stop and ask.
          setKnotState('idle');
          setPendingConfirm({
            targetType: intent.targetType,
            targetLabel: intent.targetLabel,
            itemName: intent.itemName,
            fields: intent.fields,
            rawFields: intent.fields,
            parentType: intent.parentType,
            parentLabel: intent.parentLabel,
            candidates: intent.candidates,
            suggestion: intent.suggestion,
            // CV5 §4: the confirm card is the SAME utterance, paused. It
            // carries the recording across the pause, or answering "yes" a
            // second later would lose the audio a `ready` answer keeps.
            voice: voice ? { ...voice, transcript: text } : undefined,
          });
        } catch (err) {
          // S5d §1 — the transcript is IN HAND: a 4xx/5xx from the intent
          // step (or a connection that died between the two calls) must not
          // discard the words. Losing them to a 500 is the same defect as
          // losing them to a dead radio, in a thinner disguise. The queued
          // TEXT capture is handed over by the drain like any other; the
          // receipt states it is queued.
          if (await keepOnDevice(text, [])) return;
          setKnotState('idle');
          showError(err instanceof ApiError ? err.message : 'Could not send that — try again.');
        }
      })();
    };
    recorder.stop();
  }, [cleanupAudio, onUnclear, onTranscript, onVoiceCaptured, onReadyIntent, onUpdateIntent, tenantId, clearError, showError, knotIntentEnabled]);

  /** CV4 §2 — THE ONE WAY A LIVE HOLD ENDS. Release, cancel, a pointer that
   * vanished, the app going to the background: every exit comes through
   * here, so every exit stops the recorder, submits what was said, and (via
   * the onstop -> cleanupAudio path) stops the tracks. Idempotent — the
   * second caller finds `isHolding` already false and does nothing. */
  const finishHold = useCallback(() => {
    if (!isHolding.current) return;
    isHolding.current = false;
    stopRecordingAndSubmit();
  }, [stopRecordingAndSubmit]);

  const onPointerDown = useCallback((e: PointerEvent) => {
    // CV4 §2 — RE-ENTRY GUARD. Without it a second press during a live hold
    // re-armed the timer and startRecording() built a SECOND MediaRecorder
    // over the same input; the first was never stopped and its `onstop` was
    // never assigned, so everything already spoken into it was unreachable
    // forever — and only the second recorder's audio could ever be sent.
    // iOS makes this ordinary: a second finger on the Knot, or a press that
    // lands while the first pointer's capture is still open. A press that
    // arrives while a hold is already running is not a new hold; it is
    // nothing at all, and the running hold keeps its words.
    if (isHolding.current || holdTimer.current !== null) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    pointerHandled.current = false;
    holdTimer.current = window.setTimeout(() => {
      holdTimer.current = null;
      void startRecording();
    }, HOLD_THRESHOLD_MS);
  }, [startRecording]);

  const onPointerUp = useCallback((e: PointerEvent) => {
    releaseCapture(e);
    pointerHandled.current = true;
    if (holdTimer.current !== null) {
      // Released before the hold threshold — a tap.
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
      onQuickTap();
      return;
    }
    finishHold();
  }, [onQuickTap, finishHold]);

  const onPointerCancel = useCallback((e: PointerEvent) => {
    releaseCapture(e);
    pointerHandled.current = true;
    if (holdTimer.current !== null) {
      // Cancelled before the threshold: the gesture went to the scroller
      // before it was ever a hold. Deliberately NOT treated as a tap — a
      // capture sheet appearing because someone scrolled is its own defect.
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
      return;
    }
    // CV4 §2 — CANCEL IS A RELEASE. This branch used to call recorder.stop()
    // directly, BEFORE `onstop` had ever been assigned (stopRecordingAndSubmit
    // is what assigns it), so the recorded chunks were collected and then
    // dropped on the floor: no upload, no toast, no queue entry, no receipt of
    // any kind. Words spoken and lost, in silence. And iOS fires pointercancel
    // readily — a few pixels of vertical drift mid-sentence hands the gesture
    // to the scroller. The tech SAID the thing; the only honest outcome is to
    // submit it exactly as a release does.
    finishHold();
  }, [finishHold]);

  /** CV4 §2 — the backstop for a pointer that simply vanished: capture lost
   * to a re-layout, the element removed under the finger, the browser taking
   * the pointer back. Neither `up` nor `cancel` fires afterward, so without
   * this the hold ran forever — recorder open, tracks live, the OS indicator
   * lit, and nothing left on screen to press. Ends it like a release. */
  const onLostPointerCapture = useCallback(() => {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
      return;
    }
    finishHold();
  }, [finishHold]);

  /** CV4 §2 — BACKGROUNDING MID-HOLD. Before this, the module-level release
   * stopped the tracks out from under a running recorder and left knotState
   * wherever it stood: the Knot pulsing 'listening' over a mic that was
   * already dead. There is no session stream to release now, so this handler
   * has exactly one job — end the hold the way a release does, so the words
   * are submitted and the tracks stop together, in that order. */
  const finishHoldRef = useRef(finishHold);
  finishHoldRef.current = finishHold;
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onHidden = () => {
      if (document.visibilityState === 'hidden') finishHoldRef.current();
    };
    const onPageHide = () => finishHoldRef.current();
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // Keyboard activation (Enter/Space) fires `click` with no pointer events
  // at all — treated as a tap. A completed pointer sequence ALSO fires a
  // trailing click, which must be swallowed (pointerHandled).
  const onClick = useCallback(() => {
    if (pointerHandled.current) {
      pointerHandled.current = false;
      return;
    }
    onQuickTap();
  }, [onQuickTap]);

  const resolveConfirm = useCallback(
    (parent: KnotCandidate) => {
      const pending = pendingConfirm;
      if (!pending) return;
      setPendingConfirm(null);
      setKnotState('proposing');
      void (async () => {
        try {
          const created = await knotIntentCreate(
            tenantId,
            pending.targetType,
            pending.rawFields,
            { type: pending.parentType, id: parent.id },
            pending.voice
          );
          setKnotState('confirmed');
          onVoiceCaptured?.();
          setTimeout(() => setKnotState('idle'), 900);
          if (pending.voice && created.recording == null) showError(RECORDING_LOST);
        } catch (err) {
          setKnotState('idle');
          showError(err instanceof ApiError ? err.message : 'Could not create that — try again.');
        }
      })();
    },
    [pendingConfirm, tenantId, onVoiceCaptured, showError]
  );

  const cancelConfirm = useCallback(() => setPendingConfirm(null), []);

  return {
    knotState,
    amp,
    error,
    micDenied,
    dismissError: clearError,
    pendingConfirm,
    resolveConfirm,
    cancelConfirm,
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture,
    onClick,
  };
}
